/**
 * Mobile-only "Read more" drawer (snippets/igloo-readmore.liquid, assets/igloo-readmore.css).
 * Below 750px: the collapsed part shows its top ~2 lines under a fade in the section's real background colour; the
 * button opens it with a measured height transition (no fixed heights, so customizer edits keep working).
 * From 750px: everything is reset and the CSS takes the wrapper out of the layout.
 */
(() => {
  if (customElements.get('igloo-readmore')) return;

  const mobile = window.matchMedia('(max-width: 749px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PEEK_LINES = 2;

  // Real background behind an element: the first non-transparent colour up the tree (incl. Horizon's .section-background layer)
  const backgroundOf = (el) => {
    for (let node = el; node && node !== document.documentElement; node = node.parentElement) {
      const layer = node.querySelector(':scope > .section-background');
      if (layer) {
        const c = getComputedStyle(layer).backgroundColor;
        if (c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') return c;
      }
      const c = getComputedStyle(node).backgroundColor;
      if (c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') return c;
    }
    return getComputedStyle(document.body).backgroundColor || '#ffffff';
  };

  class IglooReadMore extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('.igloo-rm__body');
      this.button = this.querySelector('.igloo-rm__toggle');
      this.label = this.querySelector('.igloo-rm__label');
      if (!this.body || !this.button) return;

      this.onToggle = () => this.toggle();
      this.onBreakpoint = () => this.setup();
      this.onEnd = (event) => {
        if (event.target !== this.body || event.propertyName !== 'max-height') return;
        if (this.classList.contains('is-open')) this.body.style.maxHeight = 'none'; // follows later content/resize changes
      };
      this.button.addEventListener('click', this.onToggle);
      this.body.addEventListener('transitionend', this.onEnd);
      mobile.addEventListener('change', this.onBreakpoint);
      this.resizeObserver = new ResizeObserver(() => {
        if (mobile.matches && !this.classList.contains('is-open')) this.measure();
      });
      this.resizeObserver.observe(this);

      // Fonts change line heights: measure again once they are in
      document.fonts?.ready.then(() => this.setup());
      this.setup();
    }

    disconnectedCallback() {
      mobile.removeEventListener('change', this.onBreakpoint);
      this.resizeObserver?.disconnect();
    }

    setup() {
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.label.textContent = this.button.dataset.more;
      this.body.style.maxHeight = '';
      if (!mobile.matches) {
        this.classList.remove('is-ready', 'is-short');
        this.style.removeProperty('--igloo-rm-peek');
        return;
      }
      this.style.setProperty('--igloo-rm-bg', backgroundOf(this));
      this.measure();
      this.classList.add('is-ready');
    }

    // Peek = the first two lines of the collapsed text; a part no taller than that isn't collapsed at all
    measure() {
      const first = this.body.querySelector('p, li, h1, h2, h3, h4, h5, h6') || this.body;
      const style = getComputedStyle(first);
      let lineHeight = parseFloat(style.lineHeight);
      if (Number.isNaN(lineHeight)) lineHeight = parseFloat(style.fontSize) * 1.5;
      const peek = Math.round(lineHeight * PEEK_LINES);
      this.style.setProperty('--igloo-rm-peek', `${peek}px`);
      this.style.setProperty('--igloo-rm-fade', `${Math.max(40, Math.min(56, Math.round(peek * 0.9)))}px`);
      this.classList.toggle('is-short', this.body.scrollHeight <= peek + lineHeight * 0.5);
    }

    toggle() {
      const open = !this.classList.contains('is-open');
      const peek = getComputedStyle(this).getPropertyValue('--igloo-rm-peek');
      if (open) {
        this.body.style.maxHeight = peek;
        this.classList.add('is-open');
        void this.body.offsetHeight;
        this.body.style.maxHeight = reduceMotion.matches ? 'none' : `${this.body.scrollHeight}px`;
      } else {
        this.body.style.maxHeight = `${this.body.scrollHeight}px`;
        void this.body.offsetHeight;
        this.classList.remove('is-open');
        this.body.style.maxHeight = '';
      }
      this.button.setAttribute('aria-expanded', String(open));
      this.label.textContent = open ? this.button.dataset.less : this.button.dataset.more;
    }
  }

  customElements.define('igloo-readmore', IglooReadMore);
})();
